/* eslint no-dupe-class-members: 0 */

import {NonFunctionKeys} from 'utility-types'
import {
  ComponentType,
  AbNode,
  AbElementNode,
  ActionNode,
  ConnectorNode,
  ConnectorElementNode,
  TimerNode,
  TriggerNode,
  Node,
  PartialNodeProps
} from '../types.ts'
import {
  isContainerNode
} from '../utils.ts'
import {walkNode} from './nodes.ts'
import { translate } from '../i18n.ts'

type DocKeys = Exclude<NonFunctionKeys<Doc>, 'version' | 'isCancelableFlow'>
type DocProps = Pick<Doc, DocKeys>

const baseNodeNames: Record<ComponentType, string> = {
  AB_SPLITTER: 'AbSplitter',
  AB_SPLITTER_ELEMENT: 'AbElement',
  ACTION: 'Action',
  CONNECTOR: 'ConditionGroup',
  CONNECTOR_ELEMENT: 'Condition',
  TIMER: 'Timer',
  TRIGGER: 'Trigger'
}

export default class Doc {
  id: number | null = null
  accountId: number | null = null;
  name!: string
  trigger!: TriggerNode
  version: number = 3

  private nextId: number = 1

  constructor(obj?: DocProps) {
    if (obj) {
      this.fillFromObject(obj)
    }
  }

  findNodeById(id: string): Node | null {
    let result: Node | null = null
    walkNode(this.trigger, node => {
      if (node.id === id) {
        result = node
        return false
      }
    })
    return result
  }

  findNodeParent(search: Node): Node | null {
    let result: Node | null = null
    walkNode(this.trigger, (node, {parent}) => {
      if (node === search) {
        result = parent
        return false
      }
    })
    return result
  }

  appendChild(parent: Node, child: Node) {
    const isContainer = isContainerNode(parent)
    if (isContainer) {
      parent.nextComponents.push(child)
    } else {
      const length = parent.nextComponents.length
      if (length > 1) {
        throw new Error(translate('error.Expected parent to have child', { num: length }))
      }
      parent.nextComponents = [child]
    }
  }

  removeChild(parent: Node, child: Node, removeChildren: boolean) {
    const index = parent.nextComponents.indexOf(child)
    if (index === -1) {
      throw new Error(translate('error.Node not child of parent'))
    }
    parent.nextComponents.splice(index, 1)
    if (!removeChildren) {
      // Append children of node being removed to parent
      parent.nextComponents.push(...child.nextComponents)
    }
  }

  replaceChild(parent: Node, oldChild: Node, newChild: Node) {
    const index = parent.nextComponents.indexOf(oldChild)
    if (index === -1) {
      throw new Error(translate('error.Node not child of parent'))
    }
    parent.nextComponents.splice(index, 1, newChild)
  }

  copyNode(node: Node): Node {
    const copy = Object.assign({}, node)
    copy.id = String(this.getNextId())
    copy.name += '-1'
    copy.nextComponents = node.nextComponents.map(child => this.copyNode(child))
    return copy
  }

  createNode(
    type: 'AB_SPLITTER',
    props?: PartialNodeProps<AbNode>,
    children?: Node[]
  ): AbNode

  createNode(
    type: 'AB_SPLITTER_ELEMENT',
    props?: PartialNodeProps<AbElementNode>,
    children?: Node[]
  ): AbElementNode

  createNode(
    type: 'ACTION',
    props?: PartialNodeProps<ActionNode>,
    children?: Node[]
  ): ActionNode

  createNode(
    type: 'CONNECTOR',
    props?: PartialNodeProps<ConnectorNode>,
    children?: Node[]
  ): ConnectorNode

  createNode(
    type: 'CONNECTOR_ELEMENT',
    props?: PartialNodeProps<ConnectorElementNode>,
    children?: Node[]
  ): ConnectorElementNode

  createNode(
    type: 'TIMER',
    props?: PartialNodeProps<TimerNode>,
    children?: Node[]
  ): TimerNode

  createNode(
    type: 'TRIGGER',
    props?: PartialNodeProps<TriggerNode>,
    children?: Node[]
  ): TriggerNode

  createNode(
    type: ComponentType,
    props?: Record<string, any>,
    children?: Node[]
  ): Node

  createNode(
    type: ComponentType,
    props: Record<string, any> = {},
    children: Node[] = []
  ): Node {
    const id = this.getNextId()
    if (type === 'AB_SPLITTER') {
      return {
        id: String(id),
        componentType: type,
        name: this.getBaseNodeName(type, id),
        nextComponents: [
          this.createNode('AB_SPLITTER_ELEMENT', {percent: 50}, children),
          this.createNode('AB_SPLITTER_ELEMENT', {percent: 50})
        ],
        ...props
      }
    }
    if (type === 'AB_SPLITTER_ELEMENT') {
      return {
        id: String(id),
        componentType: type,
        name: this.getBaseNodeName(type, id),
        nextComponents: children,
        percent: 0,
        ...props
      }
    }
    if (type === 'ACTION') {
      return {
        id: String(id),
        componentType: type,
        name: this.getBaseNodeName(type, id),
        nextComponents: children,
        actionType: null,
        actionId: null,
        dataItems: [],
        fillRecommendation: false,
        fillIntoProducts: false,
        numProductsToFill: 0,
        ...props
      }
    }
    if (type === 'CONNECTOR') {
      return {
        id: String(id),
        componentType: type,
        name: this.getBaseNodeName(type, id),
        nextComponents: [
          this.createNode('CONNECTOR_ELEMENT', {}, children)
        ],
        ...props
      }
    }
    if (type === 'CONNECTOR_ELEMENT') {
      return {
        id: String(id),
        componentType: type,
        name: this.getBaseNodeName(type, id),
        filters: [],
        nextComponents: children,
        ...props
      }
    }
    if (type === 'TIMER') {
      return {
        id: String(id),
        componentType: type,
        name: this.getBaseNodeName(type, id),
        timezone: null,
        waitUntilAttribute: null,
        waitForSeconds: null,
        waitUntilDate: null,
        preventFlowCancel: false,
        nextComponents: children,
        ...props
      }
    }
    if (type === 'TRIGGER') {
      return {
        id: String(id),
        componentType: type,
        name: this.getBaseNodeName(type, id),
        triggerType: 'SESSION_EXPIRY',
        nextComponents: children,
        ...props
      }
    }
    throw new Error(translate('error.Unknown node type', { type: `"${type}"` }))
  }

  get isCancelableFlow() {
    const triggerType = this.trigger.triggerType
    return triggerType === 'SESSION_EXPIRY' || triggerType === 'ORDER_RECEIVED'
  }

  prePublish() {
    this.updateNodeIdPaths()
  }

  // Updates the document ID and all internal node IDs.
  updateId(id: number) {
    this.id = id
    this.updateNodeIdPaths()
  }

  // Convert any node IDs that are integers to strings that include the
  // stream ID and the node IDs of all ancestors.
  updateNodeIdPaths() {
    const oldToNewPathMap: Record<string, string> = {}
    const docId = this.id
    walkNode(this.trigger, (node, {parent}) => {
      // The existing node ID will have the wrong Doc ID prefix if the document
      // has been duplicated.
      const basePath = parent
        ? oldToNewPathMap[parent.id] // The parent's path
        : docId // Or if no parent just the doc ID
      const newPath = basePath + '-' + getNodeNumber(node.id)
      oldToNewPathMap[node.id] = newPath
    })
    walkNode(this.trigger, node => {
      const newPath = oldToNewPathMap[node.id]
      node.id = newPath
    })
  }

  toJSON() {
    return {
      id: this.id,
      name: this.name,
      accountId: this.accountId,
      trigger: this.trigger,
      version: this.version
    }
  }

  private migrate() {
    const migrations = [
      this.migrateToVersion1,
      this.migrateToVersion2,
      this.migrateToVersion3
    ]
    for (let i = 0; i < migrations.length; i++) {
      const migrationVersion = i + 1
      if (this.version < migrationVersion) {
        migrations[i].apply(this)
      }
    }
  }

  // Changes from previous version:
  //  - Renamed action types.
  private migrateToVersion1() {
    this.version = 1
    const actionTypes: Record<string, string> = {
      sendToEndpoint: 'SEND_TO_ENDPOINT',
      sendNotification: 'SEND_NOTIFICATION',
      updateUserData: 'UPDATE_USER_DATA'
    }
    walkNode(this.trigger, node => {
      if (node.componentType === 'ACTION') {
        const type = actionTypes[node.actionType as any]
        if (type) {
          node.actionType = type as any
        }
      }
    })
  }

  // Changes from previous version:
  //  - Added Product Recommendation action.
  private migrateToVersion2() {
    this.version = 2
    walkNode(this.trigger, node => {
      if (node.componentType === 'ACTION') {
        node.fillRecommendation = false
        node.fillIntoProducts = false
        node.numProductsToFill = 0
      }
    })
  }

  // Changes from previous version:
  // - Using string IDs for nodes instead of integers.
  //   When publishing a stream we convert all node IDs to a string
  //   that inicludes the stream ID and the IDs of all ancestor nodes.
  private migrateToVersion3() {
    this.version = 3
    walkNode(this.trigger, node => {
      if (typeof node.id !== 'string') {
        node.id = String(node.id)
      }
    })
  }

  private getBaseNodeName(type: ComponentType, id: number): string {
    return baseNodeNames[type] + id
  }

  private calculateNextId() {
    walkNode(this.trigger, node => {
      const id = getNodeNumber(node.id)
      this.nextId = Math.max(this.nextId, id + 1)
    })
  }

  private getNextId(): number {
    return this.nextId++
  }

  private fillFromObject(obj: DocProps) {
    Object.assign(this, obj)
    if (!('version' in obj)) {
      this.version = 0
    }
    this.migrate()
    this.calculateNextId()
  }

  static fromJson(json: string): Doc {
    const doc = new Doc()
    doc.fillFromObject(JSON.parse(json))
    return doc
  }
}

// While designing a stream, nodes are all given sequential numeric IDs.
// Just prior to publishing and saving, these IDs are converted to include
// the Stream ID as well as a hyphen-separated list of node IDs that represents
// the node's hierarchy.
function isNodeIdPath(id: string): boolean {
  // Published nodes will always have at least one hyphen in them.
  return id.indexOf('-') !== -1
}

// Accepts an existing node number or path and returns the number.
// - Node Number: an integer that unique identifies the node in the document,
//   but not unique among all documents. E.g. "5"
// - Node Path: a string prefixed with the document ID and a "path" of all node
//   numbers that lead to the node being identified. E.g. "2343-1-3-5".
function getNodeNumber(idOrPath: string): number {
  const match = /(\d+)$/.exec(idOrPath)
  if (match) {
    return parseInt(match[1])
  }
  throw new Error(translate('error.Failed to get node id suffix from', { value: `"${idOrPath}"` }))
}
